import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useFormik } from "formik"
import { array, number, object, ObjectSchema, string } from "yup"

import RadioGroup from "../../../RadioGroup/RadioGroup"
import CheckboxList from "../../../Checkbox/CheckboxList"
import MultiSelectDropdown from "../../../MultiSelectDropdown/MultiSelectDropdown"
import {
    DEAL_TEMPLATES,
    DEAL_TEMPLATES_OPTIONS,
    DEAL_TYPE_OPTIONS,
    SCOPE_OF_WORK,
    SCOPE_OF_WORK_OPTIONS,
} from "../../config/projectFormConstants"
import { IDealSowForm, setDealSowForm, setDealTemplate } from "../../RaiseTicketFormSlice"
import { TStore } from "../../../../store/Store"
import { DropdownObj } from "../../../../pages/Common/commonTypes"
import { EFormModule, FormPageProps } from "../.."

import error_mark from "../../../../assets/images/error_mark.svg"

const schema: ObjectSchema<IDealSowForm> = object({
    deal_template: object({
        id: number().required(),
        title: string().required(),
    }).required(),
    deal_type: object({
        id: number().required(),
        title: string().required(),
    }).required(),
    deal_scope_of_work: array()
        .of(
            object({
                id: number().required(),
                title: string().required(),
            })
        )
        .required(),
})

const DealDetailsBeta = ({ handleNextStep, handleCloseForm }: FormPageProps) => {
    const dispatch = useDispatch()
    const { deal_sow_form, form_module } = useSelector((state: TStore) => state.RaiseTicketForm)

    const [dealTemplateOptions, setDealTemplateOptions] = useState<DropdownObj[]>(DEAL_TEMPLATES_OPTIONS)
    const [dealScopeOfWorkOptions, setDealScopeOfWorkOptions] = useState<DropdownObj[]>(SCOPE_OF_WORK_OPTIONS)

    const isProjectsModule = form_module === EFormModule.PROJECTS
    const isSurveysModule = form_module === EFormModule.SURVEYS
    const isLogisticsModule = form_module === EFormModule.LOGISTICS
    const isDemolitionModule = form_module === EFormModule.DEMOLITION
    const isEvApplicationModule = form_module === EFormModule.EV_APPLICATION

    const isDealDetailsDisabled = isSurveysModule || isLogisticsModule || isDemolitionModule || isEvApplicationModule

    const formik = useFormik({
        initialValues: deal_sow_form,
        onSubmit: handleSubmit,
        enableReinitialize: true,
        validationSchema: schema,
    })

    const handleCheckboxChange = (selectedValues: DropdownObj[]) => {
        dispatch(
            setDealSowForm({
                value: selectedValues,
                key: "deal_scope_of_work",
            })
        )
    }

    const handleDealTemplateChange = (value: DropdownObj) => {
        dispatch(setDealTemplate(value))
    }

    function handleSubmit(values: IDealSowForm) {
        handleNextStep()
    }

    useEffect(() => {
        if (isLogisticsModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.SOLO_LOGISTICS, title: "Solo Logistics" })
            handleCheckboxChange([{ id: SCOPE_OF_WORK.SOLO_LOGISTICS, title: "Solo Logistics" }])
        } else if (isSurveysModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.SOLO_SURVEY, title: "Solo Survey" })
            handleCheckboxChange([{ id: SCOPE_OF_WORK.SOLO_SURVEY, title: "Solo Survey" }])
        } else if (isDemolitionModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.DEMOLITION, title: "Demolition" })
            handleCheckboxChange([{ id: SCOPE_OF_WORK.DEMOLITION, title: "Demolition" }])
        } else if (isEvApplicationModule) {
            handleDealTemplateChange({ id: DEAL_TEMPLATES.EV_INFRA, title: "EV Infra" })
        }

        if (isProjectsModule) {
            setDealTemplateOptions(prev =>
                prev.filter(
                    option =>
                        option.id !== DEAL_TEMPLATES.SOLO_LOGISTICS &&
                        option.id !== DEAL_TEMPLATES.SOLO_SURVEY &&
                        option.id !== DEAL_TEMPLATES.DEMOLITION
                )
            )
            setDealScopeOfWorkOptions(prev =>
                prev.filter(
                    option =>
                        option.id !== SCOPE_OF_WORK.SOLO_LOGISTICS &&
                        option.id !== SCOPE_OF_WORK.SOLO_SURVEY &&
                        option.id !== SCOPE_OF_WORK.DEMOLITION
                )
            )
        }
    }, [isSurveysModule, isLogisticsModule, isProjectsModule, isEvApplicationModule, isDemolitionModule])
    return (
        <>
            <div className="deal_sow">
                <div className="section">
                    <div className="section_header">Deal Templates</div>
                    <div className="width-40p">
                        <MultiSelectDropdown
                            fontsize="12px"
                            title="Select A Template"
                            options={dealTemplateOptions}
                            id="sow_template"
                            onChange={() => {}}
                            selectedOptions={[formik.values.deal_template]}
                            selectionType={"single"}
                            onSelect={(value: DropdownObj) => {
                                formik.setFieldValue("deal_template", value)
                                handleDealTemplateChange(value)
                            }}
                            height="44px"
                            error={Boolean(formik.errors.deal_template?.title)}
                            showError={true}
                            disabled={isDealDetailsDisabled}
                        />
                    </div>
                    {formik.errors.deal_template &&
                        !!formik.values.deal_type.title &&
                        formik.values.deal_scope_of_work.length > 0 && (
                            <div className="error_message">
                                <img src={error_mark} alt="" className="pr-4" />
                                <span>No project case exists against selected Deal type and Scope of Work</span>
                            </div>
                        )}
                </div>
                <div className="section">
                    <div className="section_header">
                        Specify Deal Type?* {formik.errors.deal_type && <img src={error_mark} alt="" />}
                    </div>
                    <div>
                        <RadioGroup
                            options={DEAL_TYPE_OPTIONS}
                            name="deal_type"
                            onSelect={(value: DropdownObj) => {
                                formik.setFieldValue("deal_type", value)
                                dispatch(
                                    setDealSowForm({
                                        value: value,
                                        key: "deal_type",
                                    })
                                )
                                // dealDetailsSetter("deal_type", value);
                            }}
                            value={deal_sow_form.deal_type}
                            //   disabled={freezeField("deal_type")}
                        />
                    </div>
                </div>
                <div className="section">
                    <div className="section_header">
                        Deal's Scope of Work?* {formik.errors.deal_scope_of_work && <img src={error_mark} alt="" />}
                    </div>
                    <div>
                        <CheckboxList
                            name="deal_scope_of_work"
                            options={dealScopeOfWorkOptions}
                            onChange={value => {
                                formik.setFieldValue("deal_scope_of_work", value)
                                handleCheckboxChange(value)
                            }}
                            initialValues={formik.values.deal_scope_of_work}
                            disabled={isDealDetailsDisabled}
                            highlightChecked={
                                formik.values.deal_scope_of_work[0]?.title === "Solo Logistics" ||
                                formik.values.deal_scope_of_work[0]?.title === "Solo Survey"
                                    ? true
                                    : false
                            }
                        />
                    </div>
                </div>
            </div>

            <footer className="raise-ticket-form__footer">
                <button className="raise-ticket-form__footer-button cancel-btn" onClick={handleCloseForm}>
                    Cancel
                </button>
                <button className="raise-ticket-form__footer-button next-btn" onClick={() => formik.handleSubmit()}>
                    Next
                </button>
            </footer>
        </>
    )
}

export default DealDetailsBeta
